import { Button } from "../components/rumonai";
import { useCallback, useEffect, useRef, useState } from "react";
import { mediaKind, PublicSurface } from "../panel/PublicSurface";
import { Icon, type IconName } from "../components/Icon";
import { LightMotion } from "../components/LightMotion";
import { ApiError } from "../lib/api";
import { navigateWorkbench } from "../lib/studioNavigation";
import {
  publicationsApi, publicationState, publishedDate, workHref, type Publication, type PublicationCandidate
} from "./api";
import { PlatformDisclosure, PlatformError, usePlatformCommand, usePlatformRead } from "./shared";
import { useAutoRefresh } from "../hub/autoRefresh";
import { useStillWaiting } from "../hub/useStillWaiting";
import { BusyButton } from "../shell/BusyButton";
import { OwnerEmpty } from "../shell/OwnerEmpty";
import { PageHead } from "../shell/PageHead";
import "./publications.css";
import { agentHref, instanceStatus, type AgentInstance } from "../hub/api";

/** How long a previewed page may take to say it has drawn before the Owner is told it has not. */
export const PAGE_DRAW_WAIT_MS = 15_000;

/** A work's poster: its cover, or the clapperboard until it has one (or the cover cannot be read). */
function Poster({ url, icon = "clapperboard" }: { url?: string; icon?: IconName }) {
  const [failed, setFailed] = useState(false);
  return <div className="work-poster" aria-hidden="true">
    {url && !failed ? <img src={url} alt="" loading="lazy" onError={() => setFailed(true)} /> : <Icon name={icon} size={30} />}
  </div>;
}

/**
 * What stands where the tiles will be while they are read: the same grid and the same poster shape, so when
 * the tiles arrive the list under them does not jump down the page.
 */
function WorkTilesLoading({ label }: { label: string }) {
  return <div className="works-grid works-loading" role="status">
    <span className="visually-hidden">{label}…</span>
    {[0, 1, 2].map(n => <div className="work-tile" key={n} aria-hidden="true">
      <div className="work-poster"><LightMotion state="working" /></div>
      <div className="work-tile-copy" />
    </div>)}
  </div>;
}

/**
 * Where a press ended, drawn where it was pressed: under the tile it came from, so it is in view with the
 * control that was pressed instead of at the top of a page that has scrolled away.
 */
type Placed = { anchor: string; error?: unknown; notice?: string };
function Outcome({ placed, anchor }: { placed: Placed | null; anchor: string }) {
  if (!placed || placed.anchor !== anchor) return null;
  return placed.error ? <PlatformError error={placed.error} />
    : <p className="platform-notice work-receipt" role="status"><Icon name="done" />{placed.notice}</p>;
}

/** The manifest identifies the entire snapshot; the entry hash identifies its exact film, audio or page. */
function SnapshotVersion({ work, compact = false }: { work: Publication; compact?: boolean }) {
  const source = work.provenance;
  return <div className="work-version">
    <p className="platform-meta work-version-code">快照版本 {work.version.slice(0, 12)}</p>
    {!compact ? <>
      <PlatformDisclosure className="work-provenance" title="版本与来源">
        <dl>
          <dt>完整快照版本</dt><dd>{work.version}</dd>
          {source ? <>
            <dt>来源工作页</dt><dd>{source.panel_key}</dd>
            <dt>原文件</dt><dd>{source.entry}</dd>
            <dt>文件校验值</dt><dd>{source.entry_sha256}</dd>
            {source.source_revision && source.source_revision !== source.entry_sha256
              ? <><dt>读取时的文件版本</dt><dd>{source.source_revision}</dd></> : null}
          </> : <><dt>来源</dt><dd>此记录未提供来源信息。</dd></>}
        </dl>
        <p>这里标识保存的固定版本，工作页可能已有新的修改。</p>
        {source ? <a href={agentHref(work.agent_id, `#/panel/${encodeURIComponent(source.panel_key)}`)}>打开来源工作页</a> : null}
      </PlatformDisclosure>
    </> : null}
  </div>;
}

function candidatePublication(candidate: PublicationCandidate, rows: Publication[] | undefined) {
  return rows?.find(work => work.id === candidate.published && work.agent_id === candidate.agent_id
    && (!work.provenance || (work.provenance.panel_key === candidate.panel_key
      && (!candidate.entry || work.provenance.entry === candidate.entry))));
}

/** `address` is this page's hash; `?agent=…&panel=…` names one candidate to preview on arrival. */
export function OwnerPublications({ address = "" }: { address?: string }) {
  const agents = usePlatformRead<{ agents: AgentInstance[] }>("/api/hub/agents");
  const publications = usePlatformRead<{ publications: Publication[] }>(publicationsApi.own);
  const candidates = usePlatformRead<{
    candidates: PublicationCandidate[]; errors?: { agent_id: string; agent_name: string; message: string }[]
  }>(publicationsApi.candidates);
  const [selected, setSelected] = useState<Publication | null>(null);
  const [previewAssetBase, setPreviewAssetBase] = useState("");
  const [previewReady, setPreviewReady] = useState(false);
  const [visibilityAction, setVisibilityAction] = useState<{ work: Publication; action: "withdraw" | "restore" } | null>(null);
  // What the preview's own button came to: the work is public (the card says so where the button was), or
  // the answer never came and a new review is needed before anything is published again.
  const [outcome, setOutcome] = useState<{ published: Publication } | { error: unknown } | null>(null);
  const [placed, setPlaced] = useState<Placed | null>(null);
  const command = usePlatformCommand();
  // A page that never says it has drawn leaves the button below off for good; a film is not that kind of wait.
  const undrawn = useStillWaiting(Boolean(selected && !mediaKind(selected.entry) && !outcome && !previewReady),
    PAGE_DRAW_WAIT_MS, selected?.id);
  // The preview opens under the tile that was pressed; it is brought into view so that the film and the
  // button that makes it public are both on screen without scrolling.
  const confirmation = useRef<HTMLElement>(null);
  // A withdraw / restore asks under the work it is about, and is brought into view if that tile is low on the screen.
  const visibilityBox = useRef<HTMLDivElement>(null);
  // What the preview pushes down is drawn in its new place instead of being seen to jump there; the two
  // states are named, so going from one to the other starts the animation again (see `publications.css`).
  const wasOpen = useRef(false);
  if (selected) wasOpen.current = true;
  useEffect(() => {
    if (!selected) return;
    const node = confirmation.current;
    if (!node) return;
    const still = document.documentElement.dataset.motion === "reduced" || window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    node.scrollIntoView({ behavior: still ? "auto" : "smooth", block: "start" });
    node.focus({ preventScroll: true });
  }, [selected]);
  useEffect(() => {
    const node = visibilityBox.current;
    if (!visibilityAction || !node) return;
    const still = document.documentElement.dataset.motion === "reduced" || window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    node.scrollIntoView({ behavior: still ? "auto" : "smooth", block: "nearest" });
    node.focus({ preventScroll: true });
  }, [visibilityAction]);
  // Both lists are in hand and neither failed. A later read in the background leaves them in hand, so it
  // takes no button away; only the first read does.
  const failedPublicRead = useRef(false);
  if (publications.error) failedPublicRead.current = true;
  else if (!publications.loading && publications.data !== undefined) failedPublicRead.current = false;
  const failedCandidateRead = useRef(false);
  if (candidates.error) failedCandidateRead.current = true;
  else if (!candidates.loading && candidates.data !== undefined) failedCandidateRead.current = false;
  // Retrying a failed read has not restored its authority until a fresh answer arrives.
  const publicStateUnknown = failedPublicRead.current;
  const candidateStateUnknown = failedCandidateRead.current;
  const publicationReady = publications.data !== undefined && !publicStateUnknown;
  const ready = publicationReady && candidates.data !== undefined && !candidateStateUnknown;
  // The Hub matches the current files to a fixed publication. A shared title or
  // source alone says nothing about a new edit; an unreadable list says nothing current.
  const pendingCandidates = candidates.data?.candidates.filter(candidate =>
    !ready || !candidatePublication(candidate, publications.data?.publications)) ?? [];
  // The candidate whose preview is being prepared: its button says so, in place.
  const [preparing, setPreparing] = useState("");
  const preview = (candidate: PublicationCandidate) => {
    const key = `${candidate.agent_id}:${candidate.panel_key}`;
    setPreparing(key);
    return command.run(async () => {
      setVisibilityAction(null);
      setSelected(null);
      setOutcome(null);
      setPlaced(null);
      setPreviewReady(false);
      try {
        const result = await publicationsApi.prepare(candidate);
        setPreviewAssetBase(result.preview_asset_base);
        setSelected(result.publication);
      } catch (failure) {
        // Said under the tile that was pressed, not at the top of the page.
        setPlaced({ anchor: `candidate:${key}`, error: failure });
      }
    }).finally(() => setPreparing(""));
  };
  // Arriving from an Agent's page: open the candidate it named, once, and only
  // that one. Confirming stays a click on this page.
  const asked = new URLSearchParams(address.split("?")[1] ?? "");
  const agent = asked.get("agent") ?? "", panel = asked.get("panel") ?? "";
  const opened = useRef("");
  useEffect(() => {
    const wanted = agent && panel ? JSON.stringify([agent, panel]) : "";
    if (!wanted) { opened.current = ""; return; }
    if (!ready || opened.current === wanted) return;
    const match = candidates.data?.candidates.find(item => item.agent_id === agent && item.panel_key === panel);
    if (!match) return;
    opened.current = wanted;
    navigateWorkbench("/#/publish", true);
    // Already public: the list says so; there is nothing to preview.
    if (!match.published) preview(match);
  });
  const publish = () => {
    if (!selected || !previewReady) return undefined;
    const candidate = selected;
    return command.run(async () => {
      try {
        const result = await publicationsApi.publish(candidate);
        if (!result.publication?.available) {
          throw new ApiError("作品尚未公开", "这个版本目前不能公开访问。", "请在已发布的版本中查看状态。", 409);
        }
        setOutcome({ published: result.publication });
      } catch (failure) {
        // A missing response is not proof of a failed publication. The records are read again below, and the
        // card keeps no button that publishes this review a second time: a new review is needed.
        setOutcome({ error: failure });
      } finally {
        publications.reload();
        candidates.reload();
      }
    });
  };
  const closePreview = () => { setSelected(null); setOutcome(null); };
  // The lists read themselves again, so a work made public elsewhere appears without a button; a press in
  // flight, or a preview open under the tiles, is not moved by it.
  const reread = useCallback(() => { publications.reload(); candidates.reload(); }, [publications.reload, candidates.reload]);
  useAutoRefresh(reread, { active: !command.pending && !selected && !visibilityAction });
  const changeVisibility = () => {
    if (!visibilityAction) return undefined;
    const { work, action } = visibilityAction;
    return command.run(async () => {
      setPlaced(null);
      try {
        const result = await publicationsApi.visibility(work, action);
        if (action === "restore" && !result.publication?.available) {
          throw new ApiError("作品尚未恢复公开", "这个版本目前仍不能公开访问。", "稍后在发布记录里看当前状态。", 409);
        }
        setPlaced({ anchor: `work:${work.id}`, notice: action === "restore" ? "已恢复公开。" : "已撤回。" });
      } catch (failure) {
        setPlaced({ anchor: `work:${work.id}`, error: failure });
      } finally {
        setVisibilityAction(null);
        publications.reload();
      }
    });
  };
  // Nothing public and nothing to make public: one empty page, not two empty parts of one.
  const nothing = candidates.data !== undefined && publications.data !== undefined && !candidateStateUnknown && !publicStateUnknown
    && !candidates.data.candidates.length && !candidates.data.errors?.length && !publications.data.publications.length;
  return <div className="owner-page owner-publications">
    <PageHead
      title="公开发布"
      meta="这里管理准备公开和已公开的版本。草稿、成片与私有作品保存在各个 IP 的工作区。"
      actions={<a className="btn" href="/#/square">去广场看看<Icon name="arrow" size={16} /></a>}
    />
    {agents.error ? <PlatformError error={agents.error} retry={agents.reload} /> : null}
    {agents.data?.agents?.length ? <section className="owner-section"><h2>继续查看未公开作品</h2><ul className="platform-actions">{agents.data.agents.map(agent => <li key={agent.id}><a className="btn" href={agentHref(agent.id)}>打开 {agent.name || "未命名 IP"} 的工作区</a><span className="micro">{instanceStatus(agent).label}</span></li>)}</ul></section> : null}
    {nothing ? <OwnerEmpty icon="content" action={<a className="btn" href="/#/agents">回到我的 IP</a>}>
      还没有可公开的作品。让 IP 做出作品后，可以在这里公开它。</OwnerEmpty> : null}
    {nothing ? null : <section className="owner-section">
      <div className="owner-section-head">
        <h2>准备公开</h2>
        {pendingCandidates.length ? <span className="owner-count num">{pendingCandidates.length}</span> : null}
      </div>
      {candidates.error ? <PlatformError error={candidates.error} retry={candidates.reload} /> : null}
      {!candidates.data && candidates.loading ? <WorkTilesLoading label="正在读取可公开的作品" /> : null}
      {candidates.data?.errors?.map(error => (
        <p key={error.agent_id} className="platform-inline-warning">{error.agent_name}：{(() => { const agent = agents.data?.agents?.find(item => item.id === error.agent_id); return agent && !agent.online ? `${instanceStatus(agent).label}，本次尚未检查可公开版本。` : error.message; })()} <a href={agentHref(error.agent_id)}>打开并检查</a></p>
      ))}
      {candidates.data ? pendingCandidates.length ? (
        <div className="works-grid">
          {pendingCandidates.map(candidate => {
            const work = candidatePublication(candidate, publications.data?.publications);
            return (
              <article key={`${candidate.agent_id}:${candidate.panel_key}`} className="owner-card work-tile">
                <Poster url={candidate.cover_url} />
                <div className="work-tile-copy">
                  <p className="platform-agent-credit">{candidate.agent_name}</p>
                  <h3>{candidate.title}</h3>
                  <p className="work-tile-note">{candidate.description}</p>
                  {work ? <SnapshotVersion work={work} compact /> : null}
                  {!publicationReady ? <p className="platform-candidate-state">{publicStateUnknown ? "公开状态未知" : "公开状态待读取"}</p>
                    : candidateStateUnknown ? <p className="work-tile-note">候选版本未更新，保留上次读到的内容。</p>
                      : candidate.published && !work ? <p className="platform-candidate-state">公开记录待核对</p>
                        : !candidate.published ? <p className="work-tile-note">当前版本待预览，确认前不会公开。</p> : null}
                </div>
                {candidate.published ? (
                  <p className="platform-actions">
                    {work ? <span className="platform-candidate-state">{publicStateUnknown ? `上次读到：${publicationState(work)}` : publicationState(work)}</span> : null}
                    {work?.available ? <a className="btn" href={workHref(work.id)}>打开作品<Icon name="arrow" /></a> : null}
                  </p>
                ) : (
                  <BusyButton
                    busyLabel="正在准备…"
                    busy={command.pending && preparing === `${candidate.agent_id}:${candidate.panel_key}`}
                    disabled={!ready || (command.pending && preparing !== `${candidate.agent_id}:${candidate.panel_key}`)}
                    onPress={() => preview(candidate)}
                  >
                    准备公开<Icon name="arrow" />
                  </BusyButton>
                )}
                <Outcome placed={placed} anchor={`candidate:${candidate.agent_id}:${candidate.panel_key}`} />
              </article>
            );
          })}
        </div>
      ) : !publicationReady ? (
        <p className="platform-candidate-state" role="status">{publicStateUnknown
          ? "公开状态未知，暂时无法确认待公开作品。" : "公开状态待读取，正在核对待公开作品。"}</p>
      ) : candidateStateUnknown ? (
        <p className="platform-candidate-state">候选版本未更新，暂时无法确认待公开作品。</p>
      ) : candidates.data.errors?.length ? (
        <p className="platform-quiet-empty">部分作品尚未检查，请先处理上方提示。</p>
      ) : candidates.data.candidates.length ? (
        <p className="platform-quiet-empty">当前候选版本均已有发布记录，可在下方查看固定版本。</p>
      ) : (
        <OwnerEmpty compact icon="content" action={<a className="btn" href="/#/agents">回到我的 IP</a>}>
          现在没有可公开的作品。让 IP 为作品准备公开版本，再回到这里。</OwnerEmpty>
      ) : null}
    </section>}
    {selected ? (
      <section className="owner-card platform-confirm work-preview" aria-label="确认公开作品"
        data-preview-kind={mediaKind(selected.entry) ?? "page"} ref={confirmation} tabIndex={-1}>
        <div className="work-preview-film">
          <PublicSurface
            publication={selected}
            mode="preview"
            previewAssetBase={previewAssetBase}
            published={Boolean(outcome && "published" in outcome)}
            onReadyChange={setPreviewReady}
          />
        </div>
        <div className="work-preview-copy">
          <p className="work-preview-kicker">{outcome ? "published" in outcome ? "已公开" : "公开结果待核对"
            : selected.preview === false ? publicationState(selected) : "固定版本预览 · 尚未公开"} · {selected.agent_name}</p>
          <h2>{selected.title}</h2>
          <SnapshotVersion work={selected} />
          {outcome && "published" in outcome ? <>
            <p className="platform-notice work-receipt" role="status"><Icon name="done" />已公开到广场。</p>
            <div className="platform-actions">
              <a className="btn btn-accent" href={workHref(outcome.published.id)}>打开作品<Icon name="arrow" /></a>
              <Button variant="ghost" type="button" onClick={closePreview}>完成</Button>
            </div>
          </> : outcome ? <>
            <PlatformError error={outcome.error} />
            <div className="platform-actions">
              <Button variant="secondary" type="button" onClick={closePreview}>关闭</Button>
            </div>
          </> : <>
            <p>{selected.description}</p>
            {selected.actions?.length ? <p>访客登录后可让 IP 回应，用量由你承担，每位访客每小时最多 20 次。</p> : null}
            <p>确认后出现在广场，也可通过链接访问。</p>
            {undrawn ? <p role="status">这一页一直没画完，可以请 IP 修一修。</p> : null}
            <div className="platform-actions">
              <BusyButton className="btn btn-accent" busyLabel="正在公开…" busy={command.pending}
                disabled={!ready || !previewReady} onPress={publish}>
                确认公开到广场
              </BusyButton>
              <Button variant="secondary" type="button" disabled={command.pending} onClick={closePreview}>取消</Button>
            </div>
          </>}
        </div>
      </section>
    ) : null}
    {nothing ? null : <section className="owner-section" data-below-preview={selected ? "open" : wasOpen.current ? "closed" : undefined}>
      <div className="owner-section-head">
        <h2>已发布的版本</h2>
        {publications.data?.publications.length ? <span className="owner-count num">{publications.data.publications.length}</span> : null}
      </div>
      {publications.error ? <PlatformError error={publications.error} retry={publications.reload} /> : null}
      {!publications.data && publications.loading ? <WorkTilesLoading label="正在读取发布记录" /> : null}
      {publications.data ? publications.data.publications.length ? (
        <div className="works-grid">
          {publications.data.publications.map(work => (
            <article key={work.id} className="owner-card work-tile">
              <Poster url={work.cover_url} />
              <div className="work-tile-copy">
                <p className="platform-agent-credit">{work.agent_name}</p>
                <h3>{work.title}</h3>
                <p className="platform-meta">
                  {publishedDate(work.created_at)}
                  <span>{publicStateUnknown ? `上次读到：${publicationState(work)}` : publicationState(work)}</span>
                  {work.featured ? <span>广场精选</span> : null}
                </p>
                {publicStateUnknown ? <p className="platform-candidate-state">公开状态未知</p> : null}
                <SnapshotVersion work={work} />
                {work.numbers ? (
                  <p className="platform-meta">
                    {/* A page has no end to watch to: only a film or a sound file is watched to the end. */}
                    {mediaKind(work.entry)
                      ? `看过 ${work.numbers.viewers} · 看完 ${work.numbers.finished} · 赞 ${work.numbers.likes}`
                      : `看过 ${work.numbers.viewers} · 赞 ${work.numbers.likes}`}
                  </p>
                ) : null}
              </div>
              {visibilityAction?.work.id === work.id ? (
                <div className="work-confirm" role="group" ref={visibilityBox} tabIndex={-1}
                  aria-label={visibilityAction.action === "restore" ? "确认恢复公开" : "确认撤回作品"}>
                  <p>
                    {visibilityAction.action === "restore"
                      ? "恢复后，原链接可以打开，作品回到广场。没发布的修改不会跟着公开。"
                      : "撤回后，广场和原链接都不再提供它。IP 工作区里的原作还在。"}
                  </p>
                  <div className="platform-actions">
                    <BusyButton busy={command.pending} disabled={!publicationReady} onPress={changeVisibility}
                      busyLabel={visibilityAction.action === "restore" ? "正在恢复…" : "正在撤回…"}>
                      {visibilityAction.action === "restore" ? "确认恢复公开" : "确认撤回"}
                    </BusyButton>
                    <Button variant="ghost" type="button" disabled={command.pending}
                      onClick={() => setVisibilityAction(null)}>取消</Button>
                  </div>
                </div>
              ) : (
                <div className="platform-actions">
                  {work.available ? <a className="btn" href={workHref(work.id)}>打开作品<Icon name="arrow" /></a> : null}
                  <Button variant="ghost"

                    disabled={!publicationReady || command.pending}
                    onClick={() => {
                      setPlaced(null);
                      setVisibilityAction({ work, action: work.status === "withdrawn" ? "restore" : "withdraw" });
                      closePreview();
                    }}
                  >
                    {work.status === "withdrawn" ? "恢复公开" : "撤回公开"}
                  </Button>
                </div>
              )}
              <Outcome placed={placed} anchor={`work:${work.id}`} />
            </article>
          ))}
        </div>
      ) : (
        <p className="platform-quiet-empty">公开第一件作品后，可以在这里查看链接和管理版本。</p>
      ) : null}
    </section>}
  </div>;
}
